package com.trevorwiebe.timesheet.admin.carrier.data

import com.trevorwiebe.timesheet.admin.carrier.domain.CarrierRepository
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.dto.CarrierDto
import com.trevorwiebe.timesheet.core.domain.dto.toCarrier
import com.trevorwiebe.timesheet.core.domain.dto.toCarrierDto
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class CarrierRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository
) : CarrierRepository {

    override suspend fun getCarriers(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return kotlinx.coroutines.flow.flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val flow = firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("brokers")
            .snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<CarrierDto>().toCarrier(id = doc.id) }
                    .sortedWith(compareBy({ it.order }, { it.contactName.lowercase() }))
            }
            .map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun addCarrier(carrier: Carrier): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val result = firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("brokers")
                .add(carrier.toCarrierDto())
            TSResult(data = result.id)
        }
    }

    override suspend fun updateCarrier(carrier: Carrier): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("brokers")
                .document(carrier.id)
                .set(carrier.toCarrierDto(), merge = true)
            TSResult(data = "Carrier updated successfully")
        }
    }

    override suspend fun updateCarrierOrder(orderedIds: List<String>): TSResult {
        return errorWrapper {
            if (orderedIds.isEmpty()) return@errorWrapper TSResult(data = "Nothing to reorder")

            // A WriteBatch applies all writes atomically, but Firestore caps it at 500 ops.
            if (orderedIds.size > MAX_BATCH_OPERATIONS) {
                return@errorWrapper TSResult(
                    error = "This reorder would update ${orderedIds.size} carriers, over the " +
                        "$MAX_BATCH_OPERATIONS-record limit for an atomic write."
                )
            }

            val orgId = coreRepository.getOrganizationId().data as String
            val firestore = firebaseDatabase.firestore
            val carriersCollection = firestore
                .collection("organizations")
                .document(orgId)
                .collection("brokers")

            val batch = firestore.batch()
            orderedIds.forEachIndexed { index, carrierId ->
                batch.update(carriersCollection.document(carrierId), "order" to index)
            }
            batch.commit()

            TSResult(data = "Carrier order updated")
        }
    }

    private companion object {
        const val MAX_BATCH_OPERATIONS = 500
    }
}
